<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $t('messages.canteen'),
                        href: meta.links.index,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                    {
                        name: meta.date_display,
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <ActivityMenuOptions>
                            <GroupSelector :grade-levels="meta.links.gradeLevels" :user-grade-levels="[]">
                                <template v-slot:default="{ filteredGradeLevels }">
                                    <div class="mb-2" v-for="gradeLevel in filteredGradeLevels" :key="gradeLevel.id">
                                        <AppButton
                                            element="inertia-link"
                                            v-for="group in gradeLevel.groups"
                                            :href="group.url"
                                            :key="group.id"
                                            desired-action
                                            class="mr-2"
                                        >
                                            {{ group.name }}
                                        </AppButton>
                                    </div>
                                </template>
                            </GroupSelector>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>
        <div class="flex justify-end items-center space-x-2">
            <div class="flex justify-end" :class="form.isDirty ? 'visible' : 'invisible'">
                <div>
                    <AlertMessage class="mt-5 inline-block">
                        <template v-slot:title>
                            {{ $t('messages.warning') }}
                        </template>
                        <template v-slot:body> {{ $t('messages.unsaved_changed_warning') }} </template>
                    </AlertMessage>
                </div>
            </div>
            <AppButton element="InertiaLink" :href="meta.links.previous_month.url" v-if="meta.links.previous_month">
                &laquo; {{ meta.links.previous_month.display_name }}
            </AppButton>
            <AppButton element="InertiaLink" :href="meta.links.next_month.url" v-if="meta.links.next_month">
                {{ meta.links.next_month.display_name }} &raquo;
            </AppButton>
        </div>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <div class="flex justify-end" v-if="meta.can.create">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>

            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="max-w-xs px-2 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        <span class="sr-only">{{ $t('messages.actions') }}</span>
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        <div class="flex space-x-2 item-center">
                                            <SelectAllNoneButtons
                                                :max-count="students.length"
                                                :selected-count="selectedStudents.length"
                                                @select-all="selectAllStudents()"
                                                @select-none="selectedStudents = []"
                                                class="flex"
                                            />
                                            <span>{{ $t('messages.name') }}</span>
                                        </div>
                                    </th>
                                    <th
                                        class="max-w-xs px-2 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    ></th>
                                    <th
                                        class="max-w-sm truncate px-1 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        :class="{
                                            'border-r-2': day.is_end_of_week,
                                            'border-gray-300': day.is_end_of_week && !day.is_holiday,
                                            'bg-gray-50': index % 2 === 0 && !day.is_holiday,
                                            'bg-green-100': day.is_holiday,
                                        }"
                                        v-for="(day, index) in meta.days"
                                        :key="day.date"
                                        :title="day.is_holiday ? $tc('messages.holidays', 1) : ''"
                                    >
                                        {{ day.number }}
                                    </th>
                                    <th
                                        class="hidden max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.canteen') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.allergy') }} /
                                        {{ $t('messages.diet') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.comment') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                    v-for="(student, index) in form.students"
                                    :key="student.id"
                                >
                                    <td
                                        class="max-w-sm truncate px-2 py-2 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <AppButton
                                            element="button"
                                            type="button"
                                            size="sm"
                                            @click="copy(student)"
                                            :class="
                                                selectedStudents.length && !selectedStudents.includes(student.id)
                                                    ? 'visible'
                                                    : 'invisible'
                                            "
                                        >
                                            {{ $t('messages.copy') }}
                                        </AppButton>
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-2 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <input
                                            type="checkbox"
                                            :value="student.id"
                                            v-model="selectedStudents"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded mr-1"
                                        />
                                        <a :href="student.links.show">
                                            {{ student.full_name }}
                                        </a>
                                    </td>
                                    <td
                                        class="flex space-x-1 items-center max-w-sm truncate px-2 py-2 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <SelectAllNoneButtons
                                            :max-count="2"
                                            :selected-count="1"
                                            @select-all="selectAllDatesForStudents(student)"
                                            @select-none="student.days = []"
                                            class="flex"
                                        />
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-1 py-2 whitespace-nowrap text-sm leading-5 font-medium text-gray-900 relative"
                                        v-for="(day, index) in meta.days"
                                        :key="day.date"
                                        :class="{
                                            'border-r-2 border-gray-300': day.is_end_of_week,
                                            'bg-gray-100': index % 2 === 0,
                                        }"
                                    >
                                        <input
                                            type="checkbox"
                                            name="canteen_day[]"
                                            :value="day.date"
                                            v-model="student.days"
                                            :disabled="day.is_holiday"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                            :class="{ 'opacity-50': day.is_holiday }"
                                        />
                                        <div
                                            class="shrink-0 w-1.5 h-1.5 rounded-full bg-yellow-400 absolute bottom-0 right-0"
                                            v-if="student.unjustifiedAbsences.includes(day.date)"
                                        />
                                        <div
                                            class="shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-400 absolute bottom-0 right-0"
                                            v-if="student.justifiedAbsences.includes(day.date)"
                                        />
                                    </td>
                                    <td class="px-6 py-2 whitespace-nowrap text-sm leading-5 text-gray-500 hidden">
                                        <div class="flex space-x-1">
                                            <BadgePill
                                                v-for="day in student.days"
                                                :color="day.value ? 'indigo' : 'gray'"
                                                :key="day.text"
                                            >
                                                {{ $t(`messages.${day.text}`) }}
                                            </BadgePill>
                                        </div>
                                    </td>
                                    <td class="px-6 py-2 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        {{ student.tags }}
                                    </td>
                                    <td class="px-6 py-2 text-sm leading-5 text-gray-500">
                                        {{ student.lunch_comment }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="flex justify-end mt-5" v-if="meta.can.create">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import BadgePill from '@/components/BadgePill.vue';
import AppButton from '@/components/AppButton.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import GroupSelector from '@/components/GroupSelector.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';

export default {
    components: {
        PanelPage,
        BadgePill,
        AppButton,
        ActivityMenuOptions,
        GroupSelector,
        AlertMessage,
        SelectAllNoneButtons,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'students', 'filters', 'group'],
    data() {
        return {
            form: this.$inertia.form({
                students: this.students,
                from: this.meta.dates.from,
                to: this.meta.dates.to,
            }),
            selectedStudents: [],
        };
    },
    methods: {
        submit() {
            this.form.patch(this.meta.links.update, {
                preserveScroll: true,
                onSuccess: () => {
                    this.selectedStudents = [];
                },
            });
        },
        copy(student) {
            const days = student.days;
            this.form.students
                .filter((s) => this.selectedStudents.includes(s.id))
                .forEach((s) => {
                    s.days = days;
                });
        },
        selectAllStudents() {
            this.selectedStudents = this.students.map((s) => s.id);
        },
        selectAllDatesForStudents(student) {
            student.days = this.meta.days.filter((d) => !d.is_holiday).map((d) => d.date);
        },
    },
};
</script>
